import { TextareaAutosize as MuiTextarea } from '@mui/material';
import { cn } from 'cn';
import beautify from 'json-beautify';
import {
  type ChangeEvent,
  type FocusEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { Button } from '../Button';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = {
  readonly flex?: '1' | 'auto' | 'initial' | 'none';
  readonly hint?: string;
  readonly isDisabled?: boolean;
  readonly label?: string;
  readonly maxRows?: number;
  readonly placeholder?: string;
  readonly transformDataIncoming?: (value: unknown) => unknown;
  readonly transformDataOutgoing?: (value: unknown) => unknown;
} & ComponentStyling;

const parseJson = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatJson = (value: unknown) => {
  try {
    return typeof value === 'string'
      ? beautify(parseJson(value), null, 2, 0)
      : beautify(value, null, 2, 0);
  } catch {
    return typeof value === 'string' ? value : JSON.stringify(value) || '';
  }
};

const isValidJson = (value: string) => {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

export function JsonField({
  className,
  flex = '1',
  hint,
  isDisabled = false,
  label,
  maxRows = 10,
  placeholder,
  style,
  transformDataIncoming = (value: unknown) => value,
  transformDataOutgoing = (value: unknown) => value,
}: P) {
  const { t } = useTranslation('forms');

  const field = useFieldContext<unknown>();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = Boolean(field.state.meta.errors?.length);

  const id = useId();

  // Keep track of the raw textarea value
  const [rawValue, setRawValue] = useState(() =>
    formatJson(transformDataIncoming(field.state.value || {})),
  );
  const [isValid, setIsValid] = useState(() => isValidJson(rawValue));
  const isEditing = useRef(false);

  // Update rawValue when field.state.value changes externally
  useEffect(() => {
    // Skip updates while user is typing
    if (isEditing.current) return;

    const formattedValue = formatJson(
      transformDataIncoming(field.state.value || {}),
    );
    // Avoid comparing strings directly to prevent cursor jumping
    const parsedCurrent = isValidJson(rawValue)
      ? parseJson(rawValue)
      : rawValue;
    const parsedNew = isValidJson(formattedValue)
      ? parseJson(formattedValue)
      : formattedValue;

    // Only update if the data is actually different (comparing parsed objects, not formatted strings)
    if (JSON.stringify(parsedCurrent) !== JSON.stringify(parsedNew)) {
      setRawValue(formattedValue);
      setIsValid(isValidJson(formattedValue));
    }
  }, [field.state.value, transformDataIncoming, rawValue]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    isEditing.current = true;
    const newValue = event.target.value;
    setRawValue(newValue);
    setIsValid(isValidJson(newValue));

    // Only update the field value if the JSON data is valid
    if (isValidJson(newValue)) {
      field.handleChange(transformDataOutgoing(parseJson(newValue)));
    }
  };

  const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    isEditing.current = false;
    const value = event.target.value.trim();

    if (value === '') {
      setRawValue('');
      setIsValid(true);

      field.handleChange('');
      return;
    }

    if (isValidJson(value)) {
      const formatted = formatJson(parseJson(value));

      setRawValue(formatted);
      setIsValid(true);

      field.handleChange(parseJson(formatted));
    }
  };

  const handleFormat = () => {
    if (isValidJson(rawValue)) {
      const parsed = parseJson(rawValue);
      const formatted = formatJson(parsed);

      setRawValue(formatted);
      setIsValid(true);

      field.handleChange(parseJson(formatted));
    }
  };

  // Show the format button if the JSON data is valid and the JSON data is different from the formatted JSON data.
  const showFormatButton =
    isValid && rawValue !== formatJson(parseJson(rawValue));

  return (
    <div className={cn('relative', className)}>
      <div className="flex gap-2">
        {Boolean(label) && (
          <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
            {label}
          </InputLabel>
        )}
        <InputLabel className="ml-auto pr-1 whitespace-nowrap text-tiga-text-lighter">
          {t(($) => $.jsonField.jsonFormatHint, {
            ns: 'forms',
          })}
        </InputLabel>
      </div>
      <div
        className={cn(
          'relative flex items-center gap-x-2 transition-colors duration-200 ease-out',
          {
            'flex-auto': flex === 'auto',
            'flex-initial': flex === 'initial',
            'flex-none': flex === 'none',
          },
        )}
        style={style}
      >
        <Button
          buttonStyle="link"
          className={cn(
            'absolute top-0 right-0 z-10 transition-opacity ease-out',
            {
              'opacity-0 duration-150': !showFormatButton,
              'opacity-100 duration-600': showFormatButton,
            },
          )}
          disabled={!showFormatButton}
          text={t(($) => $.jsonField.beautifyButton.text, {
            ns: 'forms',
          })}
          onClick={handleFormat}
        />
        <MuiTextarea
          autoComplete="off"
          className={cn(
            'min-h-10 w-full flex-1 rounded-sm border border-tiga-border-default bg-white px-[14px] py-2 placeholder:pr-1 placeholder:text-tiga-text-lighter placeholder:italic focus:outline-none',
            {
              'border-tiga-semantic-error': !isValid || hasErrors,
            },
          )}
          disabled={isDisabled}
          id={id}
          maxRows={maxRows}
          minRows={1}
          placeholder={placeholder}
          value={rawValue}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </div>
      {!isValid || hasErrors ? (
        <ValidationError>
          {isValid
            ? unique(
                field.state.meta.errors?.map(({ message }) => message) ?? [],
              ).join(', ')
            : t(($) => $.jsonField.invalidJsonError, {
                ns: 'forms',
              })}
        </ValidationError>
      ) : null}
    </div>
  );
}
